"use strict";

const Rellax = require("./thirdParty/rellax.min");

/**
* Initializes various components and effects on the page.
*
* This function sets up several features to enhance the user experience,
* including scroll-triggered animations, parallax effects, carousel progress
* indicators, window scroll event handlers, and video player initializations.
*
* It is intended to be executed once the page is ready to ensure all components
* are properly initialized and functional.
*/
$(document).ready(function () {
    initMaskScrollTrigger();
    initParallax();
    initCarouselProgress();
    initWindowScroll();
    initVideoPlayers();
});

/**
* Initializes video players by attaching click event listeners to video control elements.
* Toggles play and pause states for videos within elements having the class "m-anniversary-video".
* When a control is clicked, it checks if the video is currently playing; if so, it pauses the video
* and removes the "playing" class from the control. If the video is not playing, it plays the video
* and adds the "playing" class to the control.
*/
function initVideoPlayers() {
    Array.from(document.querySelectorAll(".m-anniversary-video .video-controls")).forEach(function (controls) {
        controls.addEventListener("click", function () {
            if (controls.classList.contains("playing")) {
                controls.classList.remove("playing");
                controls.parentElement.querySelector("video").pause();
            } else {
                controls.classList.add("playing");
                controls.parentElement.querySelector("video").play();
            }
        });
    });
}

/**
* Initializes a scroll event listener on the window object.
* This function sets up the `windowScrollListener` to be triggered whenever a scroll event occurs on the window.
*/
function initWindowScroll() {
    window.addEventListener("scroll", windowScrollListener);
}

/**
* Listens for window scroll events and triggers animations for sections in view.
* This function targets sections with the class `.m-anniversary-section` that have not been animated yet.
* It checks if these sections contain any regions marked for animation and applies the specified animations
* when the section enters the viewport.
*
* The function iterates over each section, determines if it is in the viewport, and if so, applies the
* animations specified in the `data-animated` attribute of each region within the section.
* Once animations are applied, the section is marked as animated to prevent re-triggering.
*
* @returns {void} This function does not return a value.
*/
function windowScrollListener() {
    Array.from(document.querySelectorAll(".m-anniversary-section:not(.animated)")).filter(function (section) {
        return section.querySelectorAll(".m-anniversary-animated-region [data-animated]").length > 0;
    }).forEach(function (section) {
        var containerTop = section.getBoundingClientRect().top;

        // Section is in viewport
        if (!section.classList.contains("animated") && containerTop <= (window.innerHeight - (window.innerHeight * 0.3))) {
            Array.from(section.querySelectorAll(".m-anniversary-animated-region:not(.animated) .future-animation")).forEach(function (region) {
                let animations = region.getAttribute("data-animated");
                if (animations === "" || animations === null) {
                    return;
                }

                animations = animations.split(" ").map(function (animation) {
                    return animation.trim();
                });
                animateCSS(region, animations);
            });
            section.classList.add("animated");
        }
    });
}

/**
* Applies CSS animations to a DOM node and returns a Promise that resolves when the animations end.
*
* This function adds specified CSS animation classes to a given DOM node, waits for the animations
* to complete, and then removes the animation classes. It also adds an "animated" class to the node
* and removes the "data-animated" attribute once the animations are finished.
*
* @param {HTMLElement} node - The DOM node to which the animations will be applied.
* @param {Array<string>} animations - An array of CSS class names representing the animations to be applied.
* @returns {Promise<string>} A Promise that resolves with the message "Animation ended" when the animations complete.
*/
function animateCSS(node, animations) {
    // We create a Promise and return it
    new Promise((resolve, reject) => {
        node.classList.remove("future-animation");
        animations.forEach(animation => node.classList.add(animation));

        // When the animation ends, we clean the classes and resolve the Promise
        function handleAnimationEnd(event) {
            event.stopPropagation();
            animations.forEach(animation => node.classList.remove(animation));
            node.classList.add("animated");
            node.removeAttribute("data-animated");
            resolve("Animation ended");
        }

        node.addEventListener("animationend", handleAnimationEnd, {once: true});
    });
}

/**
* Initializes a scroll-triggered animation for elements with the class
* 'm-anniversary-imagemask__mask' using the GSAP library.
*
* This function registers the ScrollTrigger plugin with GSAP and sets up an
* animation that modifies the 'clipPath' property of the target elements as
* the user scrolls. The animation is triggered when the '.m-anniversary-imagemask__ctnr'
* element enters the viewport and continues until the specified scroll distance is reached.
*
* @function
* @requires gsap - The GSAP animation library.
* @requires ScrollTrigger - The GSAP ScrollTrigger plugin.
*/
function initMaskScrollTrigger() {
    gsap.registerPlugin(ScrollTrigger)
    gsap.from(".m-anniversary-imagemask__mask", {
        scrollTrigger: {
            trigger: ".m-anniversary-imagemask__ctnr",
            scrub: true,
            start: "top top",
            end: "+=100%"
        },
        clipPath: "inset(100% 0px 0px)",
        ease: "none"
    });
}

/**
* Initializes the parallax scrolling effect on elements with the class "rellax".
* Utilizes the Rellax library to create a parallax effect.
*
* Note: The DEBUG variable is defined but not used in this function.
*/
function initParallax() {
    var DEBUG = false;
    new Rellax(".rellax");
}

/**
* Initializes the progress indicators for all anniversary carousels on the page.
*
* This function selects all elements with the class `m-anniversary-carousel` that contain
* at least one navigation element with the class `m-anniversary-carousel__navigation`.
* For each of these carousels, it sets up an event listener on the `.slick-slider` element
* to update the progress indicator whenever the slide changes. It also calculates the initial
* progress when the page loads.
*
* The progress is calculated using the `calculateCarouselProgress` function, which is called
* with the current carousel container and the current slide index (starting from 1).
*
* @listens slick-slider#afterChange - Triggered after a slide change in the carousel.
* @fires calculateCarouselProgress - Calculates and updates the carousel progress.
*/
function initCarouselProgress() {
    Array.from(document.querySelectorAll(".m-anniversary-carousel")).filter(function (carouselCtnr) {
        return carouselCtnr.querySelectorAll(".m-anniversary-carousel__navigation").length > 0;
    }).forEach(function (carouselCtnr) {
        $(carouselCtnr).find(".slick-slider").on("afterChange", function (event, slick, currentSlide) {
            calculateCarouselProgress(carouselCtnr, currentSlide + 1);
        });

        // Calculate at page load too
        calculateCarouselProgress(carouselCtnr, 1);
    });
}

/**
* Updates the progress indicator for a carousel based on the current slide.
*
* This function calculates the progress percentage of a carousel by determining
* the position of the current slide relative to the total number of slides.
* It then updates the width of the progress bar to reflect this percentage.
*
* @param {HTMLElement} carouselCtnr - The container element of the carousel.
* @param {number} currentSlide - The index of the current slide (0-based).
*/
function calculateCarouselProgress(carouselCtnr, currentSlide) {
    const slidesCount = carouselCtnr.querySelectorAll(".slick-slide").length;
    const progress = (currentSlide + 1) / slidesCount * 100;
    carouselCtnr.querySelector(".m-anniversary-carousel__progress--active").style.width = `${Math.min(progress, 100)}%`;
}
